import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
import Checkbox from "@saleor/components/Checkbox";
import useNotifier from "@saleor/hooks/useNotifier";
import { findOrderEnumNames } from "@saleor/intl";
import { DialogProps } from "@saleor/types";
import {
  DeliveryTypeEnum,
  OrderVerticalType,
  SmopOrderStatusEnum
} from "@saleor/types/globalTypes";
import { generatePdf } from "@saleor/utils/pdf/handler";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { maybe } from "../../../misc";
import { OrderDetails_order } from "../../types/OrderDetails";

const useStyles = makeStyles(
  {
    buttonConfirm: {
      backgroundColor: "#FFD15C !important",
      marginLeft: "8px"
    }
  },
  {
    name: "ConfirmationDialog"
  }
);
export interface OrderConfirmationDialogProps extends DialogProps {
  motherOrderStatus: string;
  newOrderStatus?: string;
  handleSubmit: any;
  allReceivedAtFc?: boolean;
  allValidForPacking?: boolean;
  order?: OrderDetails_order;
  type: string;
  updateStatusMutation?: (newStatus: string) => void;
}

const OrderConfirmationDialog: React.FC<OrderConfirmationDialogProps> = props => {
  const {
    allReceivedAtFc,
    allValidForPacking,
    onClose,
    open,
    motherOrderStatus,
    handleSubmit,
    order,
    type,
    newOrderStatus,
    updateStatusMutation
  } = props;

  const classes = useStyles(props);
  const notify = useNotifier();
  const intl = useIntl();

  let dialogText: any;

  const grossAmount = maybe(() => order.total.gross.amount, 0);

  const formattedGrossAmount = grossAmount.toLocaleString("en-US", {
    minimumFractionDigits: 2
  });

  const rowbody = [
    [
      {
        alignment: "center",
        fontSize: 10,
        text: "QTY"
      },
      {
        alignment: "center",
        fontSize: 9,
        text: "BRAND"
      }
    ]
  ];

  const shippingItems = () => {
    for (let i = 0; order && order.subOrders.length > i; i++) {
      if (order.subOrders[i].smopStatus === SmopOrderStatusEnum.CANCELED) {
        continue;
      }
      const temp_array = [
        {
          alignment: "center",
          fontSize: 9,
          text: `${order.subOrders[i].lines.length}`
        },
        {
          alignment: "center",
          fontSize: 9,
          text: `${
            order.subOrders[i].tenant.storeName
              ? order.subOrders[i].tenant.storeName
              : "-"
          }`
        }
      ];
      rowbody.push(temp_array);
    }
    rowbody.push([
      {
        alignment: "center",
        fontSize: maybe(
          () => (order.payments[0].paymentMethodType === "gcash" ? 12 : 15),
          15
        ),
        text: `${maybe(
          () =>
            order.payments[0].paymentMethodType === "gcash"
              ? "eWallet - GCASH"
              : order.payments[0].paymentMethodType.toUpperCase(),
          ""
        )}`
      },
      {
        alignment: "center",
        fontSize: 25,
        text: `PHP ${maybe(() => formattedGrossAmount)}`
      }
    ]);
  };

  React.useEffect(() => {
    shippingItems();
  });

  switch (motherOrderStatus) {
    case "RECEIVED_AT_FC":
      if (order?.verticalType === OrderVerticalType.FOOD_AND_BEVERAGES) {
        dialogText = (
          <>
            Are you sure you want to mark this order as <b>Shipped?</b>
          </>
        );
      } else {
        if (order?.shippingMethod.deliveryType === DeliveryTypeEnum.SAME_DAY) {
          dialogText = "Are you sure you want to mark this order as Shipped?";
        } else {
          dialogText = "Are you sure you want to mark this order as Packed?";
        }
      }
      break;
    case "PACKED":
      dialogText = "Are you sure you want to mark this order as Ready to Ship?";
      break;
    case "COLLECTED":
      dialogText = "Are you sure you want to mark this order as Shipped";
      break;
    case "READY_TO_SHIP":
      dialogText = "Are you sure you want to mark this order as Shipped";
      break;
    default:
      break;
  }

  const handleConfirmDialogSubmit = () => {
    switch (type) {
      case "ORDER_DETAILS":
        if (
          maybe(() => order.shippingMethod.deliveryType) ===
            DeliveryTypeEnum.EXPRESS ||
          maybe(() => order.shippingMethod.deliveryType) ===
            DeliveryTypeEnum.EXPRESS_LONG_DISTANCE ||
          maybe(() => order.shippingMethod.deliveryType) ===
            DeliveryTypeEnum.SAME_DAY
        ) {
          if (motherOrderStatus === "RECEIVED_AT_FC") {
            updateStatusMutation(SmopOrderStatusEnum.SHIPPED);
          } else {
            handleSubmit("SHIPPED");
          }
          onClose();
        } else if (motherOrderStatus === "RECEIVED_AT_FC" && !allReceivedAtFc) {
          notify({
            status: "error",
            text: intl.formatMessage({
              defaultMessage:
                "Unable to pack. All orders must be received at FC first."
            })
          });
          onClose();
        } else if (
          motherOrderStatus === "RECEIVED_AT_FC" &&
          allReceivedAtFc &&
          !allValidForPacking
        ) {
          notify({
            status: "error",
            text: intl.formatMessage({
              defaultMessage:
                "Unable to pack. Please validate first the Parcel ID."
            })
          });
          onClose();
        } else if (
          maybe(() => order.shippingMethod.deliveryType) ===
            DeliveryTypeEnum.NATIONWIDE_DELIVERY &&
          motherOrderStatus === "RECEIVED_AT_FC" &&
          allReceivedAtFc &&
          allValidForPacking
        ) {
          if (isChecked) {
            handleSubmit("PACKED", true);
          } else {
            handleSubmit("PACKED");
          }
        } else if (
          motherOrderStatus === "RECEIVED_AT_FC" &&
          allReceivedAtFc &&
          allValidForPacking
        ) {
          if (isChecked) {
            generatePdf(
              { order },
              "/generate-shipping-label",
              true,
              "Shipping-Label-" + order.orderCode + ".pdf"
            );
          }
          handleSubmit("PACKED");
        } else if (motherOrderStatus === "PACKED") {
          handleSubmit("READY_TO_SHIP");
        } else if (
          (maybe(() => order.shippingMethod.deliveryType) ===
            DeliveryTypeEnum.NEXT_DAY ||
            maybe(() => order.shippingMethod.deliveryType) ===
              DeliveryTypeEnum.NATIONWIDE_DELIVERY) &&
          motherOrderStatus === "READY_TO_SHIP"
        ) {
          handleSubmit("SHIPPED");
        } else {
          notify({
            status: "error",
            text: intl.formatMessage({
              defaultMessage: "Something went wrong."
            })
          });
          onClose();
        }
        break;
      case "ORDER_LIST":
        handleSubmit();
        onClose();
        break;
      default:
        break;
    }
  };

  const [isChecked, setIsChecked] = useState(true);

  const handleCheckbox = event => {
    if (event.target.checked === true) {
      setIsChecked(true);
    } else if (event.target.checked === false) {
      setIsChecked(false);
    }
  };

  const checkDialog = () => (
    <Dialog onClose={onClose} open={open} maxWidth="xs" fullWidth>
      <>
        <DialogTitle>Confirm</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <div>
              {type === "ORDER_DETAILS" ? (
                <div>
                  {dialogText}
                  <div>
                    {motherOrderStatus === "RECEIVED_AT_FC" &&
                    order?.verticalType !==
                      OrderVerticalType.FOOD_AND_BEVERAGES ? (
                      <>
                        {order?.shippingMethod.deliveryType ===
                          DeliveryTypeEnum.SAME_DAY &&
                        order?.verticalType ===
                          OrderVerticalType.GENERAL_MERCHANDISE ? (
                          ""
                        ) : (
                          <>
                            <Checkbox
                              id="generateShippingLabelCheckbox"
                              checked={isChecked}
                              onChange={handleCheckbox}
                            />
                            <span>Generate Shipping Label</span>
                          </>
                        )}
                      </>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
              ) : (
                <span>
                  Are you sure you want to update status from{" "}
                  <b> {findOrderEnumNames(motherOrderStatus)}</b> to{" "}
                  <b>{findOrderEnumNames(newOrderStatus)}</b>
                </span>
              )}
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <div>
            <Button
              autoFocus
              id="cancelUpdateStatusBtn"
              onClick={onClose}
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              id="confirmUpdateStatusBtn"
              autoFocus
              onClick={handleConfirmDialogSubmit}
              color="primary"
              variant="contained"
              className={classes.buttonConfirm}
            >
              Confirm
            </Button>
          </div>
        </DialogActions>
      </>
    </Dialog>
  );
  return checkDialog();
};
OrderConfirmationDialog.displayName = "OrderConfirmationDialog";
export default OrderConfirmationDialog;
